<template>
  <PrimeMultiSelect
    ref="selectRef"
    v-model="modelValue"
    unstyled
    :pt="passThrough"
    :loading="loading"
    :options="filteredOptions"
    :placeholder="placeholder"
    :selectionLimit="selectionLimit"
    :appendTo="appendTo"
    :optionGroupLabel="optionGroupLabel"
    :optionGroupChildren="optionGroupChildren"
    :virtual-scroller-options="
      virtualScrollerOptions
        ? {
            ...virtualScrollerOptions,
            onLazyLoad: virtualLazyLoad,
            loading: loading,
          }
        : undefined
    "
    :disabled="disabled"
    :class="[sizeClass, stateClass]"
    class="meridia-select"
    v-bind="$attrs"
    @show="() => emit('show')"
    @hide="() => emit('hide')"
    @clear="() => emit('clear')"
    @blur="() => emit('blur')"
    @focus="() => emit('focus')"
  >
    <template #dropdownicon>
      <transition name="fade">
        <MeridiaIcon
          v-if="modelValue?.length > 0 && !clearDisabled"
          class="meridia-select-icon-clear"
          @click="modelValue = undefined"
          name="cancel"
          type="material-icons"
        />
      </transition>
      <MeridiaIcon
        class="meridia-select-dropdown-icon"
        :class="[
          {
            'rotate-180': selectRef?.overlayVisible,
          },
          getPt <
            PassThroughMeridiaMultipleSelect >
            ('dropdownIcon', passThrough)?.class,
        ]"
        name="keyboard_arrow_down"
        type="material-icons"
      />
    </template>
    <template #value="scope">
      <div v-if="!(scope.value?.length > 0)">
        {{ scope.placeholder }}
      </div>
      <div
        v-else
        class="whitespace-nowrap flex gap-2xs overflow-auto scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-transparent"
      >
        <div v-if="content === SelectContent.TEXT">
          {{ scope.value.map((option) => option.label).join(', ') }}
        </div>
        <div v-else v-for="(option, index) in scope.value" :key="option.value">
          <MeridiaChip
            v-if="content === SelectContent.CHIPS"
            :label="option.label"
            icon="visibility"
            removable
            @remove-clicked.stop="
              () => {
                modelValue.splice(index);
                if (modelValue.length === 0) {
                  modelValue = undefined;
                }
              }
            "
          />
          <MeridiaTag
            v-else-if="content === SelectContent.TAGS"
            :type="'risk_level'"
            :label="option.label"
            :state="'low'"
          />
        </div>
      </div>
    </template>
    <template #header>
      <div
        class="meridia-select-overlay-header"
        v-bind="
          getPt < PassThroughMeridiaMultipleSelect > ('header', passThrough)
        "
      >
        <MeridiaCheckbox
          v-if="!allSelectDisabled"
          :model-value="allSelected"
          :disabled="loading || filteredOptions?.length === 0"
          :size="'small'"
          @update:model-value="clickAll"
          class="pr-xs"
        />
        <MeridiaInput
          v-if="!filteringDisabled"
          v-model="filter"
          placeholder="Search"
          icon="search"
          class="w-full"
          :pt="
            getPt <
            PassThroughMeridiaMultipleSelect >
            ('filterInput', passThrough)
          "
        />
      </div>
    </template>
    <template #option="option">
      <MeridiaCheckbox
        v-model="option.selected"
        :label="option.option.label"
        :disabled="selectRef.isOptionDisabled(option.option)"
        :size="'small'"
        :class="{
          'bg-primary-surface-muted': option.selected,
        }"
        class="p-s cursor-pointer transition-all duration-200 w-full h-full hover:bg-neutral-surface-muted"
        v-bind="
          getPt <
          PassThroughMeridiaMultipleSelect >
          ('meridiaOption', passThrough, option)
        "
        @click.prevent
      >
        <template v-if="slots.option" #label>
          <slot
            name="option"
            :option="option.option"
            :selected="option.selected"
            :disabled="selectRef.isOptionDisabled(option.option)"
            :index="option.index"
          />
        </template>
      </MeridiaCheckbox>
    </template>
    <template #footer="{ value }">
      <div v-if="!clearDisabled" class="flex justify-end p-s">
        <MeridiaButton
          label="Clear"
          :disabled="!value"
          :size="'mini'"
          :type="'tertiary'"
          icon="filter_alt_off"
          class="w-full"
          @click="footerClearClicked"
        />
      </div>
    </template>
    <template #empty>
      <div v-if="error">Error</div>
      <div v-else class="meridia-select-no-options">No options found</div>
    </template>
    <template v-if="slots.optiongroup" #optiongroup="option">
      <slot name="optiongroup" :option="option.option" :index="option.index" />
    </template>
  </PrimeMultiSelect>
</template>

<script setup lang="ts" generic="T">
import { computed, Ref, ref, watch } from 'vue';
import {
  SelectSize,
  SelectContent,
  SelectOption,
  SelectCommonProps,
} from '../types';
import { usePassThrough } from 'primevue/passthrough';
import MeridiaCheckbox from '../../checkbox/MeridiaCheckbox.vue';
import MeridiaInput from '../../input/MeridiaInput.vue';
import MeridiaButton from '../../button/MeridiaButton.vue';
import MeridiaChip from '../../chip/MeridiaChip.vue';
import MeridiaTag from '../../tag/MeridiaTag.vue';
import _ from 'lodash';
import PrimeMultiSelect, { MultiSelectSlots } from 'primevue/multiselect';
import { useSelectUtils } from '../useSelects';
import { PassThroughMeridiaMultipleSelect } from './types';
import { getPt } from '../../utils';
import MeridiaIcon from '../../icon/MeridiaIcon.vue';

const modelValue = defineModel<SelectOption<T>[] | undefined>({
  required: true,
});

const props = withDefaults(
  defineProps<
    {
      options: SelectOption<T>[];
      selectionLimit?: number;
      allSelectDisabled?: boolean;
      pt?: PassThroughMeridiaMultipleSelect;
    } & SelectCommonProps<T>
  >(),
  {
    size: SelectSize.SMALL,
    disabled: false,
    filteringDisabled: false,
    clearDisabled: false,
    content: SelectContent.TEXT,
    placeholder: 'Select',
    selectionLimit: null,
    allSelectDisabled: false,
    appendTo: 'body',
  }
);

const emit = defineEmits<{
  (event: 'update:model-value', value: SelectOption<T>[] | undefined): void;
  (event: 'clear'): void;
  (event: 'focus'): void;
  (event: 'blur'): void;
  (event: 'show'): void;
  (event: 'hide'): void;
  (event: 'option-added', option: SelectOption<T>): void;
  (event: 'option-removed', option: SelectOption<T>): void;
}>();

const slots = defineSlots<
  {
    option: (option: {
      option: SelectOption<T>;
      selected: boolean;
      disabled: boolean;
      index: number;
    }) => any;
  } & Pick<MultiSelectSlots, 'optiongroup'>
>();

const sizeClass = computed(() => ({
  'size-small': props.size === SelectSize.SMALL,
  'size-large': props.size === SelectSize.LARGE,
}));

const stateClass = computed(() => ({
  'state-disabled': props.disabled,
  'state-seelected': modelValue.value?.length > 0,
}));

const passThrough = usePassThrough(
  props.pt,
  {
    dropdown: {
      class: 'meridia-select-dropdown-container',
    },
    labelContainer: {
      class: 'h-min flex-grow overflow-hidden',
    },
    label: {
      class: 'meridia-select-placeholder',
    },
    overlay: {
      class: 'meridia-select-overlay',
    },
    pcheadercheckbox: {
      root: {
        class: 'hidden',
      },
    },
    pcOptionCheckbox: {
      root: {
        class: 'hidden',
      },
    },
    listContainer: {
      class:
        'overflow-y-auto scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-transparent',
    },
  } as PassThroughMeridiaMultipleSelect,
  { mergeSections: true, mergeProps: true }
) as PassThroughMeridiaMultipleSelect;

const filter = ref('');
const allSelected = computed(() => {
  if (filteredOptions.value?.length === 0) return false;
  return _.isEqual(
    _.sortBy(modelValue.value?.map((v) => v.value)),
    _.sortBy(filteredOptions.value?.map((v) => v.value))
  );
});
const loading = ref(false);
const selectRef = ref<InstanceType<typeof PrimeMultiSelect> & any>(null);
const error = ref(false);
const totalCount = ref<number>(props.virtualScrollerOptions?.step ?? 30);
const filteredOptions = ref<SelectOption<T>[]>(props.options);

const { virtualLazyLoad, lazyFetchData } = useSelectUtils<T>(
  props,
  filteredOptions as Ref<SelectOption<T>[]>,
  filter,
  totalCount,
  loading,
  error
);

// Update filtered options when options change
watch(
  () => props.options,
  () => {
    filteredOptions.value = [...props.options];
  }
);

watch(
  modelValue,
  (newValue, oldValue) => {
    // Skip if both are undefined or initial setting
    if (!oldValue && !newValue) return;

    // Initialize empty arrays if undefined
    const newOptions = newValue || [];
    const oldOptions = oldValue || [];

    // Find added options (in newValue but not in oldValue)
    const addedOptions = newOptions.filter(
      (option) => !oldOptions.some((old) => old.value === option.value)
    );

    // Find removed options (in oldValue but not in newValue)
    const removedOptions = oldOptions.filter(
      (option) => !newOptions.some((newOpt) => newOpt.value === option.value)
    );

    // Emit events for each added option
    addedOptions.forEach((option) => {
      emit('option-added', option);
    });

    // Emit events for each removed option
    removedOptions.forEach((option) => {
      emit('option-removed', option);
    });
  },
  { deep: true }
);

function footerClearClicked() {
  selectRef.value.onClearClick();
  selectRef.value.hide();
}

function clickAll(value: boolean) {
  if (value) {
    modelValue.value = filteredOptions.value;
  } else {
    modelValue.value = [];
  }
}

defineExpose({
  lazyFetchData,
  filteredOptions,
});
</script>

<style src="../select.scss"></style>
